import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiError } from '../../api';
import { formatEventRange, type EventView } from '../../events/labels';
import { useClientNames } from '../../events/EventsPage';
import { DialogLayer } from '../../ui/Dialog';

/**
 * Picking the events of an offer: only the client's events (owner 2026-10-30: one client per offer), newest first.
 * Without `clientId` the client is chosen first (a new offer from „Wyceny”). `onSave` gets the chosen events (all of
 * them, ordered as listed) and the client.
 */
export function OfferEventsDialog({
  clientId: fixedClient,
  chosen,
  title = 'Wydarzenia w ofercie',
  submitLabel = 'Zapisz',
  withName = false,
  busy = false,
  error = null,
  onSave,
  onClose,
}: {
  clientId: string | null;
  chosen: readonly string[];
  title?: string;
  submitLabel?: string;
  /** A new offer: its name too. */
  withName?: boolean;
  busy?: boolean;
  error?: string | null;
  onSave: (eventIds: string[], clientId: string, name: string) => void;
  onClose: () => void;
}) {
  const names = useClientNames();
  const [clientId, setClientId] = useState<string>(fixedClient ?? '');
  const [picked, setPicked] = useState<Set<string>>(new Set(chosen));
  const [name, setName] = useState('');
  const events = useQuery({ queryKey: ['events', 'list', 'offer-picker'], queryFn: () => api<{ events: EventView[] }>('/api/events'), retry: false });
  const all = events.data?.events ?? [];
  const withClients = [...new Set(all.map((e) => e.contractorId).filter((c): c is string => !!c))].map((id) => ({ id, name: names.get(id) ?? 'Klient' })).sort((a, b) => a.name.localeCompare(b.name, 'pl'));
  const ofClient = all.filter((e) => clientId !== '' && e.contractorId === clientId).sort((a, b) => (b.startsAt ?? '').localeCompare(a.startsAt ?? ''));
  const toggle = (id: string) =>
    setPicked((p) => {
      const next = new Set(p);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const ids = ofClient.filter((e) => picked.has(e.id)).map((e) => e.id);
  return (
    <div className="dialog-backdrop">
      <form
        className="dialog panel wide offer-picker"
        role="dialog"
        aria-modal="true"
        aria-labelledby="offer-picker-title"
        onSubmit={(e) => {
          e.preventDefault();
          if (ids.length > 0) onSave(ids, clientId, name.trim());
        }}
      >
        <DialogLayer onClose={onClose} busy={busy} />
        <h2 id="offer-picker-title">{title}</h2>
        {fixedClient === null && (
          <label className="field">
            <span>Klient</span>
            <select
              value={clientId}
              onChange={(e) => {
                setClientId(e.target.value);
                setPicked(new Set());
              }}
              autoFocus
            >
              <option value="">Wybierz klienta…</option>
              {withClients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
        )}
        {withName && (
          <label className="field">
            <span>Nazwa (nieobowiązkowo)</span>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="np. Sezon jesienny 2026" autoComplete="off" />
          </label>
        )}
        {events.isLoading && <p className="muted" role="status">Wczytywanie wydarzeń…</p>}
        {clientId !== '' && !events.isLoading && ofClient.length === 0 && <p className="muted">Ten klient nie ma wydarzeń.</p>}
        {ofClient.length > 0 && (
          <fieldset className="offer-picker-list">
            <legend>Wydarzenia klienta {names.get(clientId) ?? ''}</legend>
            {ofClient.map((e) => (
              <label key={e.id} className="check">
                <input type="checkbox" checked={picked.has(e.id)} onChange={() => toggle(e.id)} />
                <span>{e.name}</span>
                <span className="muted">{formatEventRange(e.startsAt, e.endsAt)}</span>
              </label>
            ))}
          </fieldset>
        )}
        {(error || events.error) && (
          <p className="alert" role="alert">
            {error ?? (events.error instanceof ApiError ? events.error.message : 'Nie udało się wczytać wydarzeń')}
          </p>
        )}
        <div className="actions">
          <button className="btn primary" type="submit" disabled={busy || ids.length === 0}>
            {submitLabel}
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Anuluj
          </button>
        </div>
      </form>
    </div>
  );
}
